'use strict';

import jQuery from 'jquery';
import Donation from './donation.js';

var DonationModel = (function ($) {

  var defined = function (x) {
    return typeof x !== 'undefined';
  };

  function IllegalArgumentException(sMessage) {
    this.name = 'IllegalArgumentException';
    this.message = sMessage;
    this.stack = (Error()).stack;
  }
  IllegalArgumentException.prototype = Object.create(Error.prototype);
  IllegalArgumentException.prototype.constructor = IllegalArgumentException;

  function validateGift(gift) {

    // Make sure the gift has a price field
    if (!defined(gift.price)) {
      throw new IllegalArgumentException('Default gift is missing a price field');
    } else {

      // Make sure the gift has a numeric shipping value
      if (!defined(gift.price.shipping) || isNaN(gift.price.shipping)) {
        throw new IllegalArgumentException('Default gift is missing a valid shipping value');
      }

      // Make sure the gift has at least one valid price
      if (!(defined(gift.price.onetime) && !isNaN(parseFloat(gift.price.onetime))) &&
        !(defined(gift.price.sustaining) && !isNaN(parseFloat(gift.price.sustaining)))) {
        throw new IllegalArgumentException('Default gift is missing a valid price');
      }
    }

    // Make sure there is a numeric quantity
    if (!defined(gift.quantity) || (gift.quantity !== '' && isNaN(parseFloat(gift.quantity)))) {
      throw new IllegalArgumentException('Default gift is missing a valid quantity');
    }

    return true;
  }

  /**
   * Diffs two objects and take all values from object that defaults does not contain
   *
   * @param object The object we wish to diff
   * @param defaults The object containing the default values
   *
   * @returns {*} A clean values object which combines the above two objects
   */
  function removeDefaults(object, defaults) {

    if (typeof object !== 'object') {
      return object;
    }

    return Object.keys(object).reduce(
      function (collection, key) {
        var value = object[key];

        // If the value is an array
        if (Array.isArray(value)) {

          if (typeof defaults === 'undefined' || typeof defaults[key] === 'undefined' || JSON.stringify(value) !== JSON.stringify(defaults[key])) {
            collection[key] = value;
          }
          // If the value is an object
        } else if (typeof value === 'object') {
          // If the current value is an object, then all of its properties

          // If there are is a default to compare against then do the check (recursion)
          if (typeof defaults !== 'undefined') {
            value = removeDefaults(value, defaults[key]);
          }

          // If the valye still has any keys add it to the collection
          if (Object.keys(value).length > 0) {
            collection[key] = value;
          }
          // Any other value
        } else if (typeof defaults === 'undefined' || typeof defaults[key] === 'undefined' || value !== defaults[key]) {

          // It's a good value if the thing we are checking does not have this value
          collection[key] = value;
        }

        return collection;
      },
      {}
    );
  }

  // The base object for the donation form model
  var base = {
    id: '',
    version: '',
    order: {
      path: 'sustaining',
      minimumSustainingDonation: Donation.PLEDGE_MIN,
      minimumOneTimeDonation: Donation.PLEDGE_MIN,
      minimumSustainingDonationEnforced: false,
      minimumOneTimeDonationEnforced: false,
      minimumDonationCustomMessage: '',
      isOnetime: function computeIsOnetime() {
        return this.path === 'onetime';
      },
      isSustaining: function computeIsSustaining() {
        return this.path === 'sustaining';
      },
      getMinimumDonation: function getMinimumDonation() {
        if (this.isSustaining()) {
          return this.minimumSustainingDonationEnforced ? this.minimumSustainingDonation : Donation.PLEDGE_MIN;
        } else {
          return this.minimumOneTimeDonationEnforced ? this.minimumOneTimeDonation : Donation.PLEDGE_MIN;
        }
      },
      meetsSoftMinimum: function computeMeetsSoftMinimum() {
        if (this.isSustaining()) {
          return this.sustainingTotal() >= this.minimumSustainingDonation;
        } else {
          return this.onetimeTotal() >= this.minimumOneTimeDonation;
        }
      },
      meetsHardMinimum: function meetsHardMinimum() {
        if (this.isSustaining()) {
          if (!this.minimumSustainingDonationEnforced) {
            return this.sustainingTotal() >= Donation.PLEDGE_MIN;
          }
          return (this.sustainingTotal() >= this.getMinimumDonation());
        } else {
          if (!this.minimumOneTimeDonationEnforced) {
            return this.onetimeTotal() >= Donation.PLEDGE_MIN;
          }
          return (this.onetimeTotal() >= this.getMinimumDonation());
        }
      },
      minimumDonationMessage: function minimumDonationMessage() {
        if (this.isSustaining()) {
          if (!this.minimumSustainingDonationEnforced) {
            return 'Your sustaining pledge total must be at least $' + Donation.PLEDGE_MIN;
          } else {
            return 'Your sustaining pledge total must be at least $' + this.getMinimumDonation();
          }
        } else {
          if (!this.minimumOneTimeDonationEnforced) {
            return 'Your one time pledge total must be at least $' + Donation.PLEDGE_MIN;
          } else {
            return 'Your one time pledge total must be at least $' + this.getMinimumDonation();
          }
        }
      },
      gifts: [],
      popularAmounts: { 'onetime': 0, 'sustaining': 0 },
      singleAdditionalAmount: 0,
      singleOtherAmount: '',
      hasGifts: function computeHasGifts() {
        return this.orderCount() > 0;
      },
      orderCount: function computeOrderCount() {

        // Get the number of gifts in this order
        return this.gifts.reduce(
          function (total, gift) {
            return total + gift.quantity;
          },
          0
        );
      },
      addGift: function addGift(gift) {
        if (validateGift(gift)) {
          return this.gifts.push(gift);
        } else {
          return false;
        }
      },
      resetGifts: function resetGifts() {
        return this.gifts.splice(0, this.gifts.length);
      },
      singleTotal: function computeSingleTotal() {
        var otherAmount = parseFloat(this.singleOtherAmount),
          popularAmount = parseFloat(this.popularAmounts[this.path]);

        if (isNaN(otherAmount) && isNaN(popularAmount)) {
          return 0;
        } else {
          return otherAmount > 0 ? otherAmount : popularAmount;
        }
      },
      additionalTotal: function computeAdditionalTotal() {

        var additionalAmount = parseFloat(this.singleAdditionalAmount);

        return isNaN(additionalAmount) ? 0 : additionalAmount;
      },
      onetimeTotalWithShipping: function computeOnetimeTotalWithShipping() {

        // Determine the full order time order price including the shipping
        return this.onetimeTotal() + this.onetimeGiftShipping();
      },
      onetimeTotal: function computeOnetimeTotal() {
        return this.onetimeGiftTotal() + this.singleTotal() + this.additionalTotal();
      },
      onetimeGiftTotal: function computeOnetimeGiftTotal() {

        // Determine the sum one time gift price for the gifts in this order
        return this.gifts.reduce(
          function (total, gift) {
            return total + gift.price.onetime * gift.quantity;
          },
          0
        );
      },
      onetimeGiftShipping: function computeOnetimeGiftShipping() {

        // Sum up all of the shipping prices for the gifts in this order
        var shippingTotal = this.gifts.reduce(
          function (total, gift) {
            return total + gift.price.shipping;
          },
          0
        );

        // Determine the final price by running the gift total and the
        // shipping total through the custom shipping logic
        return Donation.getOnetimeShippingTotal(
          this.onetimeGiftTotal(),
          shippingTotal
        );
      },
      sustainingTotal: function computeSustainingTotal() {

        // Determine the total sustaining price for this order. Sustaining
        // orders do not incur shipping costs
        return this.gifts.reduce(
          function (total, gift) {
            return total + gift.price.sustaining * gift.quantity;
          },
          0
        ) + this.singleTotal() + this.additionalTotal();
      },
      shippingTotal: function computeShipping() {

        // Determine the shipping total for the order. Note that sustaining
        // orders never pay shipping, so it is always zero
        return this.isSustaining() ? 0 : this.onetimeGiftShipping();
      },
      qualifiesForStudioSociety: function computeStudioSocietyQualification() {
        return !this.qualifiesForPresidentSociety() && Donation.qualifiesForStudioSociety(
          this.isOnetime(),
          this.isOnetime() ? this.onetimeTotalWithShipping() : this.sustainingTotal()
        );
      },
      qualifiesForPresidentSociety: function computePresidentSocietyQualification() {
        return Donation.qualifiesForPresidentSociety(
          this.isOnetime(),
          this.isOnetime() ? this.onetimeTotalWithShipping() : this.sustainingTotal()
        );
      },
      pledgeTotal: function pledgeTotal() {
        if (this.isOnetime()) {
          return this.onetimeTotal();
        }

        return this.sustainingTotal();
      }
    },
    user: {
      account: {
        current_sustaining: '',
        lastFour: ''
      },
      billing: {
        fname: '',
        lname: '',
        email: '',
        company: '',
        member_id: '',
        fullName: function computeFullname() {
          return this.fname + ' ' + this.lname;
        }
      },
      shipping: {
        shipping_fname: '',
        shipping_lname: '',
        shipping_same: true,
        fullName: function computeFullname() {
          return this.shipping_fname + ' ' + this.shipping_lname;
        }
      },
      payment: {
        /** '' | 'cc' | 'checking' | 'paypal' */
        selection: '',

        useExistingData: false,

        hasExistingCreditData: false,
        hasExistingEFTData: false,

        credit: {
          id: function computeCreditCardId() {

            // Get the card type
            var type = this.type();

            return type.length > 0 ? type[0].id : '';
          },
          type: function computeCreditCardType() {

            // Test expressions for card types
            return Donation.getCreditCardTypes(this.number);
          },
          name: function computeCreditCardName() {

            // Get the card type
            var type = this.type();

            return type.length > 0 ? type[0].name : '';
          },
          number: '',
          expiration: {
            month: '01',
            year: ('' + (new Date()).getFullYear()).substr(-2)
          }
        },
        eft: {
          routing: '',
          account: ''
        },
        isCredit: function computeIsCredit() {
          return this.selection === 'cc';
        },
        isEFT: function computeIsEFT() {
          return this.selection === 'checking';
        },
        isPayPal: function computeIsPayPal() {
          return this.selection === 'paypal';
        },
        isExistingCredit: function isExistingCredit() {
          return this.useExistingData && this.selection === 'cc' && this.hasExistingCreditData;
        },
        isExistingEFT: function isExistingEFT() {
          return this.useExistingData && this.selection === 'checking' && this.hasExistingEFTData;
        },
        humanPaymentType() {
          let type = '';

          switch (this.selection) {
            case 'cc':
              type = 'Credit Card'
              break;
            case 'checking':
              type = 'EFT';
              break;
            case 'paypal':
              type = 'PayPal'
              break;
          }

          if (this.isExistingCredit() || this.isExistingEFT()) {
            type = 'Existing ' + type;
          }

          return type;
        }
      },
      passport: {
        token: '',
        qualifiesForPassport: function computeQualifiesForPassport() {
          return this.token.length > 0;
        },
        passportActivationRequired: false
      }
    }
  };

  var DonationModel = function DonationModel(defaults) {

    // If gifts are passed in, make sure that the required fields are set
    if (defaults &&
      defaults.order &&
      defaults.order.gifts &&
      defaults.order.gifts.length > 0) {

      // Make sure that the gifts have a price field, a quantity field, and a
      // shipping field with integer values
      defaults.order.gifts.forEach(validateGift);
    }

    return $.extend(true, this, base, defaults);
  };

  // Returns a modified state of the model that is safe for writing
  DonationModel.prototype.getSaveState = function getSaveState() {

    // Create a clone of the current user state model
    var state = $.extend(true, {}, this);

    // Drop the payment information from the state, so it is not stored
    delete state.user.payment;

    // Transform the gifts into a easier to store form
    state.order.gifts = state.order.gifts.map(function transformGift(gift) {
      return {
        id: gift.id,
        quantity: gift.quantity
      };
    });

    // Remove default values from the state model so no values are overwritten
    return removeDefaults(state, base);
  };

  DonationModel.IllegalArgumentException = IllegalArgumentException;

  return DonationModel;
})(jQuery);

export default DonationModel